import { memo } from 'react';
import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';

interface CountrySelectProps {
  /** Currently selected ISO 3166-1 alpha-2 country code, or `null`. */
  value: string | null;
  /** Called with the newly selected country code (`null` when cleared). */
  onChange: (countryCode: string | null) => void;
}

/**
 * Searchable country picker for the map configurator.
 *
 * Options come from `useCountryOptions` (localized + sorted); picking a country
 * recenters the map on its capital (handled by the parent form hook).
 */
export const CountrySelect = memo(function CountrySelect({
  value,
  onChange,
}: CountrySelectProps) {
  const { t } = useTranslation();
  const options = useCountryOptions();

  return (
    <Select
      label={t('mapSetup.country.label')}
      placeholder={t('mapSetup.country.placeholder')}
      data={options}
      value={value}
      onChange={onChange}
      nothingFoundMessage={t('mapSetup.country.notFound')}
      searchable
      comboboxProps={{
        withinPortal: true,
        zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
      }}
    />
  );
});
